import { useState, useEffect, useContext, useRef } from 'react';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import AuthContext from '../contexts/AuthContext';
import { showBrowserNotification } from '../services/browserNotificationService';

export interface Notification {
  id: string;
  type: 'like' | 'comment' | 'follow' | 'message';
  fromUserId: string;
  fromUserName: string;
  fromUserPhoto: string;
  toUserId: string;
  postId?: string;
  commentId?: string;
  content?: string;
  read: boolean;
  createdAt: Timestamp;
}

// Generar texto de notificación según el tipo
const getNotificationText = (notification: Notification): { title: string; body: string; url: string } => {
  switch (notification.type) {
    case 'like':
      return {
        title: 'DomiRed ❤️',
        body: `A ${notification.fromUserName} le gustó tu publicación`,
        url: notification.postId ? `/post/${notification.postId}` : '/'
      };
    case 'comment':
      return {
        title: 'DomiRed 💬',
        body: `${notification.fromUserName} comentó: ${notification.content?.substring(0, 50) || ''}`,
        url: notification.postId ? `/post/${notification.postId}` : '/'
      };
    case 'follow':
      return {
        title: 'DomiRed 👤',
        body: `${notification.fromUserName} ha comenzado a seguirte`,
        url: `/profile/${notification.fromUserId}`
      };
    case 'message':
      return {
        title: 'DomiRed 📩',
        body: `${notification.fromUserName}: ${notification.content?.substring(0, 50) || 'Nuevo mensaje'}`,
        url: `/messages?user=${notification.fromUserId}`
      };
    default:
      return {
        title: 'DomiRed',
        body: 'Nueva notificación',
        url: '/'
      };
  }
};

export const useNotifications = () => {
  const { currentUser } = useContext(AuthContext);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);

  // Track IDs de notificaciones ya vistas para no mostrar popup repetidos
  const seenNotificationIds = useRef<Set<string>>(new Set());
  const isFirstLoad = useRef(true);

  // Escuchar notificaciones de la base de datos
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      isFirstLoad.current = true;
      seenNotificationIds.current.clear();
      return;
    }

    setLoading(true);

    // Consulta para obtener notificaciones del usuario actual
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('toUserId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const notificationsList: Notification[] = [];
      let unread = 0;

      snapshot.forEach((doc) => {
        const notification = {
          id: doc.id,
          ...doc.data()
        } as Notification;

        notificationsList.push(notification);

        if (!notification.read) {
          unread++;
        }
      });

      // Mostrar popup/nativa para notificaciones NUEVAS no leídas
      if (isFirstLoad.current) {
        // En la primera carga, solo guardamos los IDs sin mostrar popups
        notificationsList.forEach(n => seenNotificationIds.current.add(n.id));
        isFirstLoad.current = false;
      } else {
        // En cargas siguientes, detectar notificaciones nuevas
        notificationsList.forEach(notification => {
          if (!notification.read && !seenNotificationIds.current.has(notification.id)) {
            // ¡Nueva notificación! Mostrar popup/nativa
            const { title, body, url } = getNotificationText(notification);
            showBrowserNotification(title, body, url);
          }
          seenNotificationIds.current.add(notification.id);
        });
      }

      setNotifications(notificationsList);
      setUnreadCount(unread);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Verificar permiso de notificaciones push
  useEffect(() => {
    if (!currentUser) return;

    // Verificar si estamos en el WebView de Android
    if ((window as any).Android && (window as any).Android.showNativeNotification) {
      setPushEnabled(true);
      return;
    }

    // Verificar si las notificaciones del navegador están habilitadas
    if ('Notification' in window) {
      setPushEnabled(Notification.permission === 'granted');
    }
  }, [currentUser]);

  // Solicitar permiso para notificaciones push
  const requestPushPermission = async () => {
    if (!currentUser) return false;

    // En Android WebView, ya están habilitadas nativamente
    if ((window as any).Android && (window as any).Android.showNativeNotification) {
      setPushEnabled(true);
      return true;
    }

    // En navegador, solicitar permiso
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      setPushEnabled(granted);
      return granted;
    }

    return false;
  };

  // Marcar notificación como leída
  const markAsRead = async (notificationId: string) => {
    if (!currentUser) return;

    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      });
    } catch (error) {
      console.error('Error al marcar notificación como leída:', error);
    }
  };

  // Marcar todas las notificaciones como leídas
  const markAllAsRead = async () => {
    if (!currentUser) return;

    try {
      const promises = notifications
        .filter(notification => !notification.read)
        .map(notification =>
          updateDoc(doc(db, 'notifications', notification.id), { read: true })
        );

      await Promise.all(promises);
    } catch (error) {
      console.error('Error al marcar todas las notificaciones como leídas:', error);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    pushEnabled,
    requestPushPermission,
    markAsRead,
    markAllAsRead
  };
};
